import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../template/store';
import { IDataTableColumn } from '../../common/types/dataTable';
import DataTable from '../../common/components/DataTable';
import UploadThing from '../../../components/common/UploadThing';
import { openDocumentFromUrl } from '../../../helpers/openDocumentInNewTab';
import { removeCustomerDocument, updateCustomerListByRowNumber, updateSelectedCustomerAttachmentName, updateSelectedCustomerAttachmentFilePath, updateSelectedCustomerFileServerPath } from './InvoiceRequestSlice';
import { useTranslation } from 'react-i18next';
import { encryptAndEncode } from '../../../encrypt-util';

interface InvoiceRequestCustomerDocumentsProps {
    isViewMode?: boolean;
    customerLoadingFileState: Record<string, any>;
    setCustomerLoadingFileState: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

const InvoiceRequestCustomerDocuments: React.FC<InvoiceRequestCustomerDocumentsProps> = ({
    isViewMode = false,
    customerLoadingFileState,
    setCustomerLoadingFileState,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
    const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
    const customerDocAttachment = useSelector((state: RootState) => state.invoiceRequest.customerAttData);
    const customerDocList = useSelector((state: RootState) => state.invoiceRequest.customerDocList);
    const customerDropdownDocList = useSelector((state: RootState) => state.invoiceRequest.customerDropdownDocList);
    const selectedCustomerAttachmentName = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerAttachmentName);
    const selectedCustomerAttachmentFilePath = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerAttachmentFilePath);
    const customerServerFilePath = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerFileServerPath);
    const { t, i18n } = useTranslation();
    const UploadCell = (rowData: any) => {
        const { attachmentId, fileName, rowNumber } = rowData.row.original;
        const key = attachmentId ?? rowNumber ?? null;
        const customerDocLink = `${process.env.REACT_APP_CLIENT_APP_HOST}/api/edo-rest-v2/v2/common/openDocument?id=${encryptAndEncode(attachmentId)}&moduleTp=INVOICE_REQUEST_INSTRUCTION&linerId=${encryptAndEncode(profileDetails?.partnerType === 'liner' ? profileDetails?.partnerId : selectedLinerId)}&rd=N`;

        const charLimit = (name: string, limit: number) =>
            name && name?.length >= limit ? name?.substring(0, limit) + '...' : name;
        const disableFields = isViewMode;

        if (!disableFields) {
            return (
                <div className='d-flex justify-content-start align-items-center'>
                    <UploadThing
                        name={
                            selectedCustomerAttachmentFilePath?.[key]
                                ? fileName
                                : selectedCustomerAttachmentName?.[key]?.name
                        }
                        loading={customerLoadingFileState[key]}
                        disabled={Object.values(customerLoadingFileState).some(
                            (val) => val != 100 && val != -1 && val != undefined
                        )}
                        fulfilled={(name: string, serverPath: string, serverName: string) => {
                            setCustomerLoadingFileState((state: any) => ({ ...state, [key]: 100 }));
                            dispatch(
                                updateSelectedCustomerFileServerPath({
                                    ...customerServerFilePath,
                                    [key]: [serverPath, serverName],
                                })
                            );
                            dispatch(
                                updateSelectedCustomerAttachmentName({
                                    ...selectedCustomerAttachmentName,
                                    [key]: { name },
                                })
                            );
                        }}
                        failed={() => {
                            setCustomerLoadingFileState((state: any) => ({ ...state, [key]: -1 }));
                            dispatch(
                                updateSelectedCustomerAttachmentName({
                                    ...selectedCustomerAttachmentName,
                                    [key]: { name: '' },
                                })
                            );
                        }}
                        pending={(loading: number) => {
                            setCustomerLoadingFileState((state: any) => ({ ...state, [key]: loading }));
                        }}
                        clear={() => undefined}
                        selected={(name: string) => {
                            dispatch(
                                updateSelectedCustomerAttachmentName({
                                    ...selectedCustomerAttachmentName,
                                    [key]: { name },
                                })
                            );
                            setCustomerLoadingFileState((state: any) => ({ ...state, [key]: 0 }));
                            dispatch(
                                updateSelectedCustomerAttachmentFilePath({
                                    ...selectedCustomerAttachmentFilePath,
                                    [key]: null,
                                })
                            );
                        }}
                        fileLimit={10}
                        fileType={['PDF']}
                        labelWidth='250px'
                        link={
                            !selectedCustomerAttachmentFilePath?.[key] ? undefined : customerDocLink
                        }
                        allowRemove={false}
                    />
                </div>
            );
        }

        return (
            <div className='d-flex justify-content-start align-items-center'>
                <span
                    className='text-primary cursor-pointer'
                    onClick={() => openDocumentFromUrl(customerDocLink)}
                >
                    {charLimit(fileName, 50)}
                </span>
            </div>
        );
    };

    const RemoveCell = (rowData: any) => {
        const { isDropDownDoc, rowNumber, attachmentId } = rowData.row.original;
        if (!isDropDownDoc || attachmentId) return null;
        const isAnyUploading = Object.values(customerLoadingFileState).some(
            (val) => val != 100 && val != -1 && val != undefined
        );
        return (
            <Button
                variant='falcon-warning'
                style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
                disabled={isAnyUploading}
                onClick={() => {
                    dispatch(removeCustomerDocument({ rowNumber }));
                    const { [rowNumber]: _removed, ...rest } = customerLoadingFileState ?? {};
                    setCustomerLoadingFileState(Object.keys(rest).length > 0 ? rest : {});
                }}
            >
                <FontAwesomeIcon icon={faTrash} />
            </Button>
        );
    };

    const DocNameCell = (rowData: any) => {
        const { docId, isDropDownDoc, rowNumber, attachmentId } = rowData.row.original;
        const customerDocName = customerDocList?.find((v: any) => v?.id === docId)?.name;
        const isCustomerDocMandatory = customerDocList?.find((v: any) => v?.id === docId)?.m;
        const isAnyUploading = Object.values(customerLoadingFileState).some(
            (val) => val != 100 && val != -1 && val != undefined
        );

        if (isDropDownDoc && !attachmentId) {
            return (
                <div className='d-flex flex-row gx3'>
                    <span className='mandatory'>*</span>
                    <Form.Select
                        value={docId != null ? String(docId) : '0'}
                        className='fs--1 form-control form-select'
                        disabled={isAnyUploading || attachmentId}
                        onChange={(e) => {
                            const selectedId = Number(e.target.value);
                            const selectedOption = customerDropdownDocList?.find(
                                (opt: any) => opt.id === selectedId
                            );
                            if (!selectedOption) return;
                            dispatch(
                                updateCustomerListByRowNumber({
                                    rowNumber,
                                    updatedFields: {
                                        id: selectedOption.id,
                                        name: selectedOption.name,
                                    },
                                })
                            );
                        }}
                    >
                        <option value='0' disabled>
                            Select a document
                        </option>
                        {customerDropdownDocList?.map((option: any) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            );
        }

        return (
            <>
                {isCustomerDocMandatory && <span className='mandatory'>*</span>}
                <span title={customerDocName || ''}>{customerDocName || ''}</span>
            </>
        );
    };

    const columns: IDataTableColumn[] = [
        { accessor: 'attachmentId', Header: 'attachmentId' },
        {
            accessor: 'docId',
            Header: t('resource_2:documentName'),
            Cell: DocNameCell,
            width: 200,
            maxWidth: 180,
        },
        {
            accessor: 'byteArray',
            Header: t('resource_1:upload'),
            Cell: UploadCell,
            width: 350,
            maxWidth: 350,
        },
        {
            accessor: 'remove',
            Header: '',
            Cell: RemoveCell,
            width: 50,
            maxWidth: 50,
        },
    ];

    return (
        <div className='col-lg-12 mt-2'>
            {(customerDocAttachment?.list?.length ?? 0) > 0 && (customerDocList?.length ?? 0) > 0 && (
                <div className='col-lg-12 demmurageTable mt-1'>
                    <DataTable
                        data={customerDocAttachment?.list ?? []}
                        gridName='INVOICE_REQUEST_CUSTOMER_ATTACHMENT'
                        columns={columns}
                        sortable={false}
                        searchable={false}
                        showColumnFilter={false}
                        selection={false}
                        perPage={100}
                        pagination={false}
                        showExport={false}
                        uniqueFilterKey='invoiceRequestCustomerAttachment'
                        autoHeight={true}
                        staticHiddenCols={['attachmentId']}
                    />
                </div>
            )}
        </div>
    );
};

export default InvoiceRequestCustomerDocuments;
